/**
 * Limpieza de markdown inline para el pipeline de voz (Fase speak).
 *
 * Problema que resuelve: el coach a veces emite markdown (`**on**`, `*nice*`,
 * `` `present perfect` ``) en su reply. Si ese texto llega crudo a un motor TTS
 * (ElevenLabs), los símbolos producen artefactos de audio — el motor intenta
 * vocalizar los asteriscos/backticks. La regla dura: **nunca mandar markdown a
 * un TTS**.
 *
 * Diseño (ver docs/architecture/04-voice-pipeline.md):
 *   - El TEXTO HABLADO y el TEXTO VISIBLE deben ser el MISMO string limpio (sin
 *     marcadores), para que el karaoke — que mapea el índice de carácter del
 *     alignment de ElevenLabs sobre el texto mostrado — no se desincronice. La
 *     negrita es una CAPA de formato (rangos), no caracteres dentro del texto.
 *   - Por eso esta util es compartida (`@orborix/shared-types`): el backend la
 *     usa para limpiar lo que va al TTS (alignment queda en coordenadas
 *     limpias) y el cliente la usa para renderizar el mismo texto limpio +
 *     pintar los rangos en negrita. Ambos lados DEBEN producir el `clean`
 *     idéntico carácter por carácter, o el karaoke deriva.
 *
 * Chunk-safety: el backend llama a {@link stripMarkdownForSpeech} por cada
 * delta del stream del LLM. La limpieza es pura remoción local de caracteres
 * (`*`, `` ` ``, `~`), así que limpiar por chunks y concatenar produce el mismo
 * resultado que limpiar el texto completo — aunque un `**` quede partido entre
 * chunks (`**o` + `n**` → `o` + `n` → `on`). El emparejado de negrita (que sí
 * necesita el texto completo) sólo lo usa el cliente, que siempre tiene el
 * `assistantText` acumulado.
 *
 * Alcance deliberadamente acotado (KISS): cubrimos el énfasis inline que el
 * modelo realmente emite — `*`/`**` (cursiva/negrita), `` ` `` (code) y `~`
 * (strikethrough). NO tocamos `_` (riesgo de mutilar palabras), ni `-`/`#`/`>`
 * de inicio de línea (un `-` es casi siempre un guión legítimo en prosa).
 */

/** Rango `[start, end)` en coordenadas del texto LIMPIO (end exclusivo). */
export type BoldRange = readonly [start: number, end: number];

export interface ParsedInlineMarkdown {
  /** Texto sin marcadores de markdown. Es lo que va al TTS y lo que se muestra. */
  readonly clean: string;
  /** Rangos a renderizar en negrita, en coordenadas de `clean`. Ordenados, sin solapar. */
  readonly bold: ReadonlyArray<BoldRange>;
}

/**
 * Parsea markdown inline → texto limpio + rangos de negrita.
 *
 * Reglas:
 *   - Un run de 2+ asteriscos (`**`) togglea negrita; el run se elimina.
 *   - Un asterisco solo (`*`) es cursiva → se elimina sin generar rango (sólo
 *     nos interesa la negrita para el render).
 *   - `` ` `` y `~` se eliminan (code / strikethrough) sin rango.
 *   - Una negrita sin cerrar (`**on` mientras todavía streamea) NO emite rango
 *     hasta que llegue el `**` de cierre — así el render no parpadea a negrita
 *     a mitad de palabra.
 */
export function parseInlineMarkdown(input: string): ParsedInlineMarkdown {
  let clean = '';
  const bold: BoldRange[] = [];
  let boldActive = false;
  let boldStart = -1;

  let i = 0;
  while (i < input.length) {
    const c = input[i];

    if (c === '*') {
      // Consumir el run completo de asteriscos.
      let j = i + 1;
      while (j < input.length && input[j] === '*') j += 1;
      const runLen = j - i;
      if (runLen >= 2) {
        if (!boldActive) {
          boldActive = true;
          boldStart = clean.length;
        } else {
          boldActive = false;
          if (clean.length > boldStart) bold.push([boldStart, clean.length]);
        }
      }
      // runLen === 1 (cursiva): se elimina sin togglear negrita.
      i = j;
      continue;
    }

    if (c === '`' || c === '~') {
      i += 1; // strip code / strikethrough markers
      continue;
    }

    clean += c;
    i += 1;
  }

  // Negrita sin cerrar → se ignora el rango (queda como texto plano). El
  // próximo render, ya con el `**` de cierre, lo pintará.
  return { clean, bold };
}

/**
 * Texto listo para TTS: markdown inline removido. Equivale a
 * `parseInlineMarkdown(input).clean` — definido así a propósito para garantizar
 * que el `clean` del backend (TTS) y el del cliente (render + karaoke) sean
 * idénticos byte a byte.
 */
export function stripMarkdownForSpeech(input: string): string {
  return parseInlineMarkdown(input).clean;
}
